/* ──────────────────────────────────────────────────────────────
   src/components/reuniones/ReunionReglasTable.tsx
   Tabla sencilla de reglas de agendación
   ────────────────────────────────────────────────────────────── */
import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ReunionReglaAgendacion } from '../../api/reunionReglas.api';

export interface ReunionReglasTableProps {
  rows: ReunionReglaAgendacion[];
  loading?: boolean;
  onEdit?: (row: ReunionReglaAgendacion) => void;
  onDelete?: (row: ReunionReglaAgendacion) => void;
}

export default function ReunionReglasTable({
  rows,
  loading = false,
  onEdit,
  onDelete,
}: ReunionReglasTableProps) {
  const { t } = useTranslation<'translation'>();

  return (
    <TableContainer component={Paper} sx={{ position: 'relative' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.5)',
            zIndex: 1,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.name', 'Nombre'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.type', 'Tipo de reunión'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.role', 'Rol destino'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.channel', 'Canal preferente'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.user', 'Usuario'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.center', 'Centro'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.daysFromEvent', 'Días desde evento'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.duration', 'Duración (min)'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.recurrent', 'Recurrente'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {String(t('reunionRules.columns.active', 'Activa'))}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              {String(t('common.acciones', 'Acciones'))}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={11}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  {String(t('reunionRules.empty', 'No hay reglas configuradas todavía.'))}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {rows.map((r) => {
            const rangeLabel =
              r.diasDesdeEventoMin != null || r.diasDesdeEventoMax != null
                ? `${r.diasDesdeEventoMin ?? '0'} – ${r.diasDesdeEventoMax ?? '?'}`
                : '—';

            return (
              <TableRow key={r.id} hover>
                <TableCell>{r.nombre ?? '—'}</TableCell>
                <TableCell>{r.tipoReunion}</TableCell>
                <TableCell>{r.rolStaffDestino}</TableCell>
                <TableCell>{r.canalPreferente ?? '—'}</TableCell>
                <TableCell>
                  {r.usuario?.nombreCompleto ?? (r.idUsuario ? `#${r.idUsuario}` : '—')}
                </TableCell>
                <TableCell>
                  {r.centro?.nombre ?? (r.idCentro ? `Centro ${r.idCentro}` : '—')}
                </TableCell>
                <TableCell>{rangeLabel}</TableCell>
                <TableCell>{r.duracionMinutos ?? '—'}</TableCell>
                <TableCell>
                  {r.esRecurrente ? (
                    <Chip
                      size="small"
                      label={`${r.frecuenciaSemanas ?? 1} sem.`}
                      sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      r.activa
                        ? String(t('reunionRules.active.yes', 'Activa'))
                        : String(t('reunionRules.active.no', 'Inactiva'))
                    }
                    sx={{
                      bgcolor: r.activa ? '#21a598' : '#e0e0e0',
                      color: r.activa ? '#fff' : '#555',
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  {onEdit && (
                    <IconButton
                      size="small"
                      onClick={() => onEdit(r)}
                      aria-label={String(t('common.edit', 'Editar'))}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                  {onDelete && (
                    <IconButton
                      size="small"
                      onClick={() => onDelete(r)}
                      aria-label={String(t('common.delete', 'Eliminar'))}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
